import { Flex, Heading } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { GmailColorIcon } from 'ui-components/dist/GmailColorIcon';
import { FEATURE_FLAGS, VIEW_OPTIONS } from '~/constants';
import { useCopyEmbed } from '~/hooks/api/useCopyEmbed';
import { useGetSavedListCreatorsAll } from '~/hooks/api/useSavedLists';
import { useCreateSharedLink } from '~/hooks/api/useSavedLists/useCreateSharedLink';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { useSavedListsStore } from '~/screens/MyAgency/store';

import { useEmbedListIframeModalStore } from '../EmbedListIframeModal/EmbedListIframeModal.store';
import { EmbedButton } from './EmbedButton';

const FrameIconTemporary = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="22" x2="2" y1="6" y2="6" />
    <line x1="22" x2="2" y1="18" y2="18" />
    <line x1="6" x2="6" y1="2" y2="22" />
    <line x1="18" x2="18" y1="2" y2="22" />
  </svg>
);

type EmbedsProps = {
  listId: string;
  onClose?: () => void;
};

export const Embeds = ({ listId, onClose }: EmbedsProps) => {
  const [isLoadingEmbedIframe, setIsLoadingEmbedIframe] = useState(false);
  const { creators } = useSavedListsStore();
  const { setIsOpen, setSharedToken } = useEmbedListIframeModalStore();

  const { creators: savedListCreators, isLoading } = useGetSavedListCreatorsAll(
    { id: listId },
  );

  const { isEnabled: isShareableWidgetEnabled } = useFeature(
    FEATURE_FLAGS.foamShareableWidget,
  );

  const {
    isBasicLoading,
    isDetailLoading,
    triggerBasicEmbedCopy,
    triggerDetailEmbedCopy,
  } = useCopyEmbed({
    isLoading,
    listId,
    savedListCreators,
    creators,
  });

  const { mutateAsync: createSharedLink } = useCreateSharedLink();

  const handleIframeEmbedCopy = useCallback(async () => {
    setIsLoadingEmbedIframe(true);

    const sharedLink = await createSharedLink({
      listId,
      options: {
        audienceFiltersHidden: true,
        view: VIEW_OPTIONS.list,
      },
      isEmailRequired: false,
    });

    const sharedToken = sharedLink?.split('/').pop();

    onClose?.();

    if (sharedToken) {
      setIsOpen(true);
      setSharedToken(sharedToken);
    }

    setIsLoadingEmbedIframe(false);
  }, [listId, createSharedLink, setIsOpen, setSharedToken]);

  return (
    <>
      <Heading
        as="h3"
        size="lg"
        fontSize="lg"
        fontWeight="medium"
        lineHeight="4.5"
      >
        Embeds
      </Heading>
      <Flex flexWrap="wrap" gap={2} marginTop={2}>
        <EmbedButton
          sx={{
            flex: {
              base: '1 0 100%',
              sm: 'auto',
            },
            flexGrow: '1',
            width: { base: '100%', sm: '45%' },
          }}
          icon={GmailColorIcon}
          label="Copy basic embed"
          onClick={triggerBasicEmbedCopy}
          track-id="foam-share-list-basic-embed"
          isLoading={isBasicLoading}
        />
        <EmbedButton
          sx={{
            flex: {
              base: '1 0 100%',
              sm: 'auto',
            },
            flexGrow: '1',
            width: { base: '100%', sm: '45%' },
          }}
          icon={GmailColorIcon}
          label="Copy detail embed"
          onClick={triggerDetailEmbedCopy}
          track-id="foam-share-share-list-detail-embed"
          isLoading={isDetailLoading}
        />
        {isShareableWidgetEnabled && (
          <EmbedButton
            sx={{
              flex: {
                base: '1 0 100%',
                sm: 'auto',
              },
              flexGrow: '1',
              width: { base: '100%', sm: '45%' },
            }}
            icon={FrameIconTemporary}
            isLoading={isLoadingEmbedIframe}
            label="Create Embeddable Widget"
            onClick={handleIframeEmbedCopy}
            track-id="foam_create_embed_widget_button"
          />
        )}
        {/* TODO: Use these SmartButton components once it's fixed */}
        {/* <SmartButton
          sx={{
            flex: {
              base: '1 0 100%',
              sm: 'auto',
            },
            flexGrow: '1',
            width: { base: '100%', sm: '45%' },
          }}
          leftIcon={<GmailColorIcon />}
          onClick={triggerBasicEmbedCopy}
          status={isBasicLoading ? 'loading' : 'idle'}
          successText="Embeds copied!"
          track-id="foam-share-list-basic-embed"
        >
          Copy basic embed
        </SmartButton> */}
        {/* <SmartButton
          sx={{
            flex: {
              base: '1 0 100%',
              sm: 'auto',
            },
            flexGrow: '1',
            width: { base: '100%', sm: '45%' },
          }}
          leftIcon={<GmailColorIcon />}
          onClick={triggerDetailEmbedCopy}
          status={isDetailLoading ? 'loading' : 'idle'}
          successText="Embeds copied!"
          track-id="foam-share-share-list-detail-embed"
        >
          Copy detail embed
        </SmartButton> */}
      </Flex>
    </>
  );
};
